import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { UserContext } from './User';

/**
 * Plan/add-on entitlements — the store's "switchboard" (SaaS launch, Focus 2).
 *
 * Fetches GET /api/entitlements once per session for the ACTIVE store (a
 * store switch does a full reload, so no re-fetch wiring is needed) and
 * exposes:
 *
 *   hasFeature(key)      -> should this feature WORK right now?
 *   featureState(key)    -> 'on' | 'locked' | 'hidden' | 'coming_soon' — how to render it
 *   limits               -> { limit_listings: {max, used}, limit_seats: {…} }
 *   plan / addons / mode
 *
 * Deliberately FAIL-OPEN: while loading, on fetch error, or while the
 * backend mode is 'off'/'shadow', hasFeature answers true — gating must
 * never lock a paying user out because of a hiccup, and must act only when
 * the platform is explicitly in enforce mode. featureState() ignores mode so
 * admin/settings UIs can display true lock state even before enforcement.
 *
 * The one exception is COMING SOON (Alex, 2026-10-27): those keys are
 * DAO-Watches-only and gate in every mode — the backend already answers
 * per store, so the frontend just believes it.
 *
 * Distinct from FeatureGating.jsx (the legacy per-user merchant quota popup)
 * — that path stays untouched; this one is per-store.
 */

const defaultValue = {
  loading: true,
  mode: 'off',
  plan: null,
  addons: [],
  features: {},
  limits: {},
  catalog: [],
  failed: false,
  gatingActive: false,
  platformStaff: false,
  hasFeature: () => true,
  featureState: () => 'on',
  refresh: () => {},
};

export const EntitlementsContext = createContext(defaultValue);

export function EntitlementsProvider({ children }) {
  const { user } = useContext(UserContext);
  const [state, setState] = useState({ loading: true, data: null, failed: false });

  const refresh = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const { data } = await axios.get('/api/entitlements');
      setState({ loading: false, data, failed: false });
    } catch (e) {
      // Fail open for GATING — no data means nothing gets hidden or locked.
      //
      // But record that it FAILED, separately from "there is no plan". Those
      // were the same thing before, and Your Plan renders nothing when there
      // is no plan — so one hiccup on load made the panel vanish for the rest
      // of the session, with no error and no retry. It looked random
      // (Alex, 2026-10-26).
      setState({ loading: false, data: null, failed: true });
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      refresh();
    } else {
      setState({ loading: false, data: null, failed: false });
    }
  }, [user?.id, refresh]);

  const value = useMemo(() => {
    const data = state.data;
    const mode = data?.mode || 'off';
    const gatingActive = mode === 'enforce';
    const features = data?.features || {};
    const catalog = data?.catalog || [];
    const futureKeys = new Set(catalog.filter((f) => f.state === 'future').map((f) => f.key));
    const comingSoonKeys = new Set(catalog.filter((f) => f.state === 'coming_soon').map((f) => f.key));

    const availableOne = (key) => {
      if (!data) return true;
      // Unknown key -> fail open (a frontend ahead of the backend catalogue
      // must not lock its own users out).
      if (!(key in features)) return true;
      return features[key] !== false;
    };
    // 'a|b' = ANY of them, the same convention as the backend `feature:`
    // middleware — e.g. Stock alert opens with Stock alert OR Arbitrages.
    const available = (key) => String(key).split('|').some(availableOne);

    return {
      loading: state.loading,
      // True only when the fetch ERRORED — distinct from a store that
      // genuinely has no plan, which is a legitimate empty state.
      failed: Boolean(state.failed),
      mode,
      plan: data?.plan ?? null,
      addons: data?.addons || [],
      features,
      limits: data?.limits || {},
      catalog,
      gatingActive,
      // DAO Watches staff — Trade Feed and Deals open (canUseOpenedFeature).
      platformStaff: Boolean(data?.platform_staff),

      // Mode-aware: only enforce mode actually withholds a feature — except
      // coming-soon keys, which gate in every mode.
      hasFeature: (key) => (!gatingActive && !String(key).split('|').some((k) => comingSoonKeys.has(k)) ? true : available(key)),

      // Mode-blind render treatment: 'on' | 'locked' (upgrade prompt) |
      // 'hidden' (Future Release — never advertised) | 'coming_soon' (no
      // upgrade can unlock it yet). Callers that hide nav items should
      // additionally check gatingActive before acting on 'locked'/'hidden'.
      featureState: (key) => {
        if (available(key)) return 'on';
        const first = String(key).split('|')[0];
        if (comingSoonKeys.has(first)) return 'coming_soon';
        return futureKeys.has(first) ? 'hidden' : 'locked';
      },

      refresh,
    };
  }, [state, refresh]);

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

/** Convenience hook: const { hasFeature, limits } = useEntitlements(); */
export function useEntitlements() {
  return useContext(EntitlementsContext);
}

/** Convenience hook for one key: const canLookbook = useEntitlement('lookbook_generation'); */
export function useEntitlement(key) {
  const { hasFeature } = useContext(EntitlementsContext);
  return hasFeature(key);
}

export default EntitlementsProvider;
